import clsx from 'clsx';
import { CSSProperties, ReactElement } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { chunkArrayInGroups } from '@vestico/utils';

import { ClientReviewResponse } from '@vestico/model';

import { BorderRadiusStyles, GlobalsContext } from '@vestico/shared';

import { ShowcaseItem } from './ShowcaseItem';
import style from './SnakeLayoutRow.module.css';

export enum SnakeLayoutRowType {
  simpleRow,
  largeTileLeft,
  largeTileRight,
  largeTileCenterLeft,
  largeTileCenterRight,
}

export interface SnakeLayoutRowProps {
  type: SnakeLayoutRowType;
  responsesPerRow: number;
  responses: ClientReviewResponse[];
  previewTextsMap: Map<string, string | undefined>;
  showLikes?: boolean;
  onResponseClick: (responseIndex: number) => void;
  updateInView: (responseIndex: number, inView: boolean) => void;
  firstInViewVideoResponse?: ClientReviewResponse;
  containerWidth: number;
  borderRadius: BorderRadiusStyles;
  customItemCSS: CSSProperties;
}

const ITEM_PADDING_MOBILE = 8;
const ITEM_PADDING_DESKTOP = 12;
const SMALL_TILES_PER_COLUMN = 2;

export const SnakeLayoutRow = ({
  type,
  responsesPerRow,
  responses,
  previewTextsMap,
  onResponseClick,
  updateInView,
  firstInViewVideoResponse,
  showLikes,
  containerWidth,
  borderRadius,
  customItemCSS,
}: SnakeLayoutRowProps) => {
  // Response came back but there's nothing to show
  const responsesCount = responses.length;

  if (responsesCount > responsesPerRow) {
    console.error(`Expected max ${responsesPerRow} items for snake layout row`);
  }

  const onClickResponse = useCallback(
    (r: ClientReviewResponse) => () => {
      const index = responses.indexOf(r);
      if (index === -1) {
        return;
      }
      onResponseClick(index);
    },
    [responses, onResponseClick]
  );

  const onUpdateInView = useCallback(
    (r: ClientReviewResponse) => (inView: boolean) => {
      const index = responses.indexOf(r);
      if (index === -1) {
        return;
      }
      updateInView(index, inView);
    },
    [responses, updateInView]
  );

  if (responsesCount < responsesPerRow) {
    type = SnakeLayoutRowType.simpleRow;
  }

  const { useMobileLayout } = useContext(GlobalsContext);
  const itemPadding = useMemo(() => (useMobileLayout ? ITEM_PADDING_MOBILE : ITEM_PADDING_DESKTOP), [useMobileLayout]);

  const getSmallTilesCountPerRow = useCallback(() => {
    if (responsesCount < 3) {
      return responsesCount;
    }
    // Add 3 to represent large tile size
    return (responsesCount + 3) / SMALL_TILES_PER_COLUMN;
  }, [responsesCount]);

  const getSmallItemSize = useCallback(() => {
    const smallTilesPerRow = getSmallTilesCountPerRow();
    const itemSize = (1 / smallTilesPerRow) * (containerWidth - itemPadding * (smallTilesPerRow - 1));
    return itemSize;
  }, [getSmallTilesCountPerRow, containerWidth, itemPadding]);

  const [smallItemSize, setSmallItemSize] = useState(getSmallItemSize());

  const getLargeItemSize = useCallback(() => {
    const smallTilesPerRow = getSmallTilesCountPerRow();
    const itemSize =
      containerWidth - itemPadding * (smallTilesPerRow - SMALL_TILES_PER_COLUMN) - smallItemSize * (smallTilesPerRow - SMALL_TILES_PER_COLUMN);
    return itemSize;
  }, [containerWidth, getSmallTilesCountPerRow, itemPadding, smallItemSize]);

  useEffect(() => {
    setSmallItemSize(getSmallItemSize());
  }, [containerWidth, getSmallItemSize, responses]);

  const getItemMarkup = useCallback(
    (isSmall: boolean, r: ClientReviewResponse) => {
      const itemSize = isSmall ? smallItemSize : getLargeItemSize();
      return (
        <div key={r.postDocumentID} style={{ width: itemSize, height: itemSize }}>
          <ShowcaseItem
            response={r}
            onClick={onClickResponse(r)}
            previewText={previewTextsMap.get(r.postDocumentID)}
            showLikes={showLikes}
            aspectRatio="square"
            updateInView={onUpdateInView(r)}
            playVideo={firstInViewVideoResponse === r}
            borderRadius={borderRadius}
            customCSS={customItemCSS}
          />
        </div>
      );
    },
    [
      smallItemSize,
      getLargeItemSize,
      onClickResponse,
      previewTextsMap,
      showLikes,
      onUpdateInView,
      firstInViewVideoResponse,
      borderRadius,
      customItemCSS,
    ]
  );

  const getThinVertical = useCallback(
    (thinResponses: ClientReviewResponse[]) => {
      return <div className={clsx(style.verticalColumn)}>{thinResponses.map((r) => getItemMarkup(true, r))}</div>;
    },
    [getItemMarkup]
  );

  const getVerticalColumns = useCallback(
    (thinResponses: ClientReviewResponse[]) => {
      const columns = chunkArrayInGroups(thinResponses, 2);
      const verticalColumns = columns.map((column) => getThinVertical(column));
      return verticalColumns;
    },
    [getThinVertical]
  );

  let responseMarkups: ReactElement[];
  switch (type) {
    case SnakeLayoutRowType.simpleRow:
      responseMarkups = responses.map((r) => getItemMarkup(true, r));
      break;
    case SnakeLayoutRowType.largeTileLeft:
      {
        const largeTile = getItemMarkup(false, responses[0]);
        const remainingResponses = responses.slice(1);
        const verticalColumns = getVerticalColumns(remainingResponses);
        responseMarkups = [largeTile, ...verticalColumns];
      }
      break;
    case SnakeLayoutRowType.largeTileRight:
      {
        const largeTile = getItemMarkup(false, responses[responsesCount - 1]);
        const remainingResponses = responses.slice(0, responsesCount - 1);
        const verticalColumns = getVerticalColumns(remainingResponses);
        responseMarkups = [...verticalColumns, largeTile];
      }
      break;
    case SnakeLayoutRowType.largeTileCenterLeft:
      {
        const leftVerticalColumn = getThinVertical(responses.slice(0, 2));
        const largeTile = getItemMarkup(false, responses[2]);
        const remainingResponses = responses.slice(3, responsesCount);
        const verticalColumns = getVerticalColumns(remainingResponses);
        responseMarkups = [leftVerticalColumn, largeTile, ...verticalColumns];
      }
      break;
    case SnakeLayoutRowType.largeTileCenterRight:
      {
        const maxVerticalColumnsIndex = responsesCount - 4;
        const remainingResponses = responses.slice(0, maxVerticalColumnsIndex + 1);
        const verticalColumns = getVerticalColumns(remainingResponses);
        const largeTile = getItemMarkup(false, responses[maxVerticalColumnsIndex + 1]);
        const rightVerticalColumn = getThinVertical(responses.slice(maxVerticalColumnsIndex + 2, responsesCount));
        responseMarkups = [...verticalColumns, largeTile, rightVerticalColumn];
      }
      break;
    default:
      throw new Error(`Unhandled type ${type}`);
  }

  if (responsesCount === 0) {
    return null;
  }

  return (
    <div className={clsx(style.root)} style={{ '--showcase-snake-item-padding': `${itemPadding}px` } as CSSProperties}>
      {responseMarkups}
    </div>
  );
};
